import React from "react";
import { Loader2 } from "lucide-react";

import {
  BROKER_FIELD_DESC_CLASS,
  BROKER_ALERT_TITLE_CLASS,
} from "@/components/Broker/brokerAlertUi";
import { StrategyPineDetailDialog } from "@/components/Strategies/StrategyPineDetailDialog";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { DashboardLayoutDialogFooter } from "@/views/Dashboard/Dashboard/components/DashboardLayoutDialogFooter";

export const REMOVE_BROKER_CREDENTIALS_CONFIRM_PHRASE = "remove credentials";
export const DISCONNECT_BROKER_OAUTH_CONFIRM_PHRASE = "disconnect";

export type RemoveBrokerCredentialsDialogMode = "credentials" | "oauth";

export interface RemoveBrokerCredentialsDialogProps {
  open: boolean;
  brokerDisplayName: string;
  /** API-key brokers use credentials; cTrader OAuth uses disconnect. */
  mode?: RemoveBrokerCredentialsDialogMode;
  confirmText: string;
  onConfirmTextChange: (value: string) => void;
  isRemoving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function RemoveBrokerCredentialsDialog({
  open,
  brokerDisplayName,
  mode = "credentials",
  confirmText,
  onConfirmTextChange,
  isRemoving,
  onCancel,
  onConfirm,
}: RemoveBrokerCredentialsDialogProps) {
  const isOauth = mode === "oauth";
  const confirmPhrase = isOauth
    ? DISCONNECT_BROKER_OAUTH_CONFIRM_PHRASE
    : REMOVE_BROKER_CREDENTIALS_CONFIRM_PHRASE;
  const confirmMatches = confirmText.trim().toLowerCase() === confirmPhrase;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isRemoving) onCancel();
  };

  return (
    <StrategyPineDetailDialog
      open={open}
      onOpenChange={handleOpenChange}
      title={isOauth ? "Disconnect" : "Remove credentials"}
      showHeader={false}
      showDismissButton={false}
      contentClassName="sm:max-w-md"
      bodyClassName="flex-none overflow-visible"
      footer={
        <DashboardLayoutDialogFooter onCancel={onCancel}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-font="primary"
            data-trade-log-action="delete"
            className="shrink-0 border-destructive/40 text-destructive shadow-sm hover:bg-destructive/10 hover:text-destructive focus-visible:border-destructive/60 focus-visible:ring-destructive/25 dark:border-destructive/50 dark:hover:bg-destructive/15 [&_svg]:text-current"
            disabled={!confirmMatches || isRemoving}
            aria-busy={isRemoving || undefined}
            onClick={onConfirm}
          >
            {isRemoving ? (
              <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
            ) : (
              <span className="normal-case tracking-normal">
                {isOauth ? "Disconnect" : "Remove credentials"}
              </span>
            )}
          </Button>
        </DashboardLayoutDialogFooter>
      }
    >
      {open ? (
        <Field className="gap-2">
          <FieldLabel
            htmlFor="broker-remove-credentials-confirm"
            className={BROKER_ALERT_TITLE_CLASS}
          >
            {isOauth ? "Disconnect OAuth" : "Remove API Credentials"}
          </FieldLabel>
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
            {isOauth ? (
              <>
                This disconnects{" "}
                <span className="font-medium text-[#6b7280]">{brokerDisplayName}</span>{" "}
                OAuth access from Tessera. Connected accounts and Tessera trade data stay. Reconnect
                later to sync again. Enter &lsquo;disconnect&rsquo; to confirm.
              </>
            ) : (
              <>
                This removes saved login/API credentials for{" "}
                <span className="font-medium text-[#6b7280]">{brokerDisplayName}</span>. Connected
                accounts and Tessera trade data stay. You can add credentials again later to resync.
                Enter &lsquo;remove credentials&rsquo; to confirm.
              </>
            )}
          </FieldDescription>
          <Input
            id="broker-remove-credentials-confirm"
            value={confirmText}
            onChange={(event) => onConfirmTextChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape" && !isRemoving) onCancel();
            }}
            placeholder={
              isOauth
                ? "Type 'disconnect' to confirm"
                : "Type 'remove credentials' to confirm"
            }
            autoFocus
            disabled={isRemoving}
            autoComplete="off"
            data-font="primary"
          />
        </Field>
      ) : null}
    </StrategyPineDetailDialog>
  );
}
